if (!customElements.get('h7-currency-recommendation')) {
  customElements.define(
    'h7-currency-recommendation',
    class H7CurrencyRecommendation extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.countryName = this.querySelector('[data-h7-currency-country]');
        this.currencyCode = this.querySelector('[data-h7-currency-code]');
        this.buttonCurrencyCode = this.querySelector('[data-h7-currency-button-code]');
        this.countryInput = this.querySelector('[data-h7-currency-country-input]');
        this.dismissButton = this.querySelector('[data-h7-currency-dismiss]');
        this.form = this.querySelector('form');
        if (!this.countryName || !this.currencyCode || !this.buttonCurrencyCode || !this.countryInput) return;
        this.dataset.ready = 'true';
        this.countries = this.readCountries();
        this.dismissButton?.addEventListener('click', () => this.dismiss());
        this.form?.addEventListener('submit', () => this.rememberCurrency());
        // discover() manages the overlay lock itself and must never bubble a
        // rejection out of connectedCallback.
        this.discover().catch(() => {
          window.H7OverlayCoordinator?.release('h7-currency-recommendation');
        });
      }

      readCountries() {
        try {
          return JSON.parse(this.querySelector('[data-h7-currency-countries]')?.textContent || '{}');
        } catch (_error) {
          return {};
        }
      }

      async discover() {
        let detectedCountry;

        try {
          const endpoint = new URL(this.dataset.endpoint, window.location.origin);
          endpoint.searchParams.set('country[enabled]', 'true');
          endpoint.searchParams.set('country[exclude]', this.dataset.currentCountry || '');
          endpoint.searchParams.set('language[enabled]', 'false');
          const response = await fetch(endpoint.toString(), { headers: { Accept: 'application/json' } });
          if (response.ok) {
            const suggestion = await response.json();
            detectedCountry = suggestion?.detected_values?.country;
          }
        } catch (_error) {
          detectedCountry = undefined;
        }

        const fallbackCountryCode = this.browserCountryCode();
        const countryCode = detectedCountry?.handle || fallbackCountryCode;
        const country = this.countries[countryCode];
        if (this.explicitSelectionPending) return;
        if (this.hasSelectedCurrency()) return;
        if (!country || country.currency === this.dataset.currentCurrency) return;

        this.suggestedCountryCode = countryCode;
        this.dismissKey = `h7-currency-recommendation:${countryCode}:${country.currency}`;
        if (this.wasDismissed()) return;

        await window.H7OverlayCoordinator?.request('h7-currency-recommendation', { priority: 1 });
        if (this.explicitSelectionPending) {
          window.H7OverlayCoordinator?.release('h7-currency-recommendation');
          return;
        }
        if (this.hasSelectedCurrency() || this.wasDismissed()) {
          window.H7OverlayCoordinator?.release('h7-currency-recommendation');
          return;
        }

        try {
          const displayName = detectedCountry?.name || country.name;
          this.countryName.textContent = displayName;
          this.currencyCode.textContent = country.currency;
          this.buttonCurrencyCode.textContent = country.currency;
          this.countryInput.value = countryCode;
          this.removeAttribute('hidden');
        } catch (error) {
          // Anything failing after the lock is acquired must release it, or
          // every other overlay on the page stays queued forever.
          window.H7OverlayCoordinator?.release('h7-currency-recommendation');
        }
      }

      promptForCountrySelection({ countryCode, countryName, currency }) {
        if (!countryCode || !currency || currency === this.dataset.currentCurrency) return false;

        const show = () => {
          this.explicitSelectionPending = true;
          this.suggestedCountryCode = countryCode;
          this.suggestedCurrency = currency;
          this.countryName.textContent = countryName || this.countries[countryCode]?.name || countryCode;
          this.currencyCode.textContent = currency;
          this.buttonCurrencyCode.textContent = currency;
          this.countryInput.value = countryCode;
          this.removeAttribute('hidden');
          this.form?.querySelector('button[type="submit"]')?.focus();
        };

        const safeShow = () => {
          try {
            show();
          } catch (error) {
            window.H7OverlayCoordinator?.release('h7-currency-recommendation');
          }
        };
        const request = window.H7OverlayCoordinator?.request('h7-currency-recommendation', { priority: 20 });
        if (request) request.then(safeShow);
        else safeShow();
        return true;
      }

      rememberCurrency() {
        const currency = this.suggestedCurrency || this.countries[this.countryInput?.value]?.currency;
        if (!currency) return;

        try {
          window.localStorage.setItem('h7-selected-currency', currency);
        } catch (_error) {
          // The country and currency switch still submits when storage is unavailable.
        }
      }

      browserCountryCode() {
        const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
        for (const language of languages) {
          const region = language?.split('-').reverse().find((part) => /^[A-Z]{2}$/i.test(part));
          const countryCode = region?.toUpperCase();
          if (countryCode && this.countries[countryCode]) return countryCode;
        }
        return undefined;
      }

      hasSelectedCurrency() {
        try {
          return /^[A-Z]{3}$/.test(window.localStorage.getItem('h7-selected-currency') || '');
        } catch (_error) {
          return false;
        }
      }

      wasDismissed() {
        try {
          return window.sessionStorage.getItem(this.dismissKey) === 'dismissed';
        } catch (_error) {
          return false;
        }
      }

      dismiss() {
        try {
          if (this.dismissKey) window.sessionStorage.setItem(this.dismissKey, 'dismissed');
        } catch (_error) {
          // The prompt can still be dismissed when storage is unavailable.
        }
        this.explicitSelectionPending = false;
        this.suggestedCurrency = undefined;
        this.setAttribute('hidden', '');
        window.H7OverlayCoordinator?.release('h7-currency-recommendation');
      }
    }
  );
}
